import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { TargetedShiftSpec } from "../../../../employment/targetedShiftSalaries/targetedShiftSalariesApi";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { ConfirmationDialog } from "../../../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import useMessage from "../../../../../hooks/useMessage";
import {
  AddAssignment,
  doAcceptWorkAgreement,
  OfferWorkAgreement,
  OrderRowBundleId,
  postAddAssignment,
  postOfferWorkAgreement,
  UpdateWorkAgreement,
} from "../../../orderAPI";
import {
  cancelWorkAgreementOffer,
  selectOrderRowById,
  updateWorkAgreement,
} from "../../../orderRowsDetailedSlice";
import { SelectedTalent } from "../../../types/editOrder";
import {
  createAcceptWorkAgreementApiPayload,
  createAddAssignmentApiPayload,
  createApiPayload,
  FormSchedulingClassIds,
  WorkAgreementFormValues,
  WorkOfferType,
} from "./helpers";
import { AdditionalInfo } from "./sections/AdditionalInfo";
import { BasicInfo } from "./sections/BasicInfo";
import { CreateHrTaskWhenAccepted } from "./sections/CreateHrTaskWhenAccepted";
import { FormActions } from "./sections/FormActions";
import { Message } from "./sections/Message";
import { OverrideBillingPrice } from "./sections/OverrideBillingPrice";
import { Salary } from "./sections/Salary";
import { Supplements } from "./sections/Supplements";
import { TermsOfEmployment } from "./sections/TermsOfEmployment";
import { WorkingTime } from "./sections/WorkingTime";
import { SupplementPricingOverrides } from "./sections/SupplementPricingOverrides";
import { WorkAgreementTargetedShifts } from "./sections/WorkAgreementTargetedShifts";
import { OverlappableEmployment } from "../../../../talents/talentAPI";
import { clearReplacementWorkAgreementData } from "../../../../../app/appSlice";
import OverlaySpinner from "../../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { WorkAgreementDocument } from "./sections/workAgreementDocument/WorkAgreementDocument";
import {
  WORK_AGREEMENT_ACCEPTED,
  WORK_AGREEMENT_DECLINED,
} from "../../../constants";

interface ConfirmationDialogContent {
  title: string;
  contentText: string;
  confirmText: string;
  action: () => void;
}

interface Props {
  bundleId: OrderRowBundleId;
  talent: SelectedTalent;
  defaultValues: WorkAgreementFormValues;
  version: number;
  navigateToOrderDetails: () => void;
  type: WorkOfferType;
  sourceEmployment?: OverlappableEmployment;
  schedulingClassIds: FormSchedulingClassIds;
  targetedShiftSpec?: TargetedShiftSpec | null;
  setHasSystemGeneratedDocument: (hasSystemGeneratedDocument: boolean) => void;
  hasSystemGeneratedDocument: boolean;
}

export const WorkAgreementForm = ({
  bundleId,
  talent,
  defaultValues,
  version,
  navigateToOrderDetails,
  type,
  sourceEmployment,
  schedulingClassIds,
  targetedShiftSpec,
  setHasSystemGeneratedDocument,
  hasSystemGeneratedDocument,
}: Props) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useAppDispatch();
  const { workAgreementId } = useParams();
  const { sendMessage } = useMessage();

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const { handleSubmit, getValues, reset } = methods;

  const isReadOnly: boolean =
    defaultValues.status === WORK_AGREEMENT_ACCEPTED ||
    defaultValues.status === WORK_AGREEMENT_DECLINED ||
    type !== WorkOfferType.New;

  const [periodStartDateIsRequired, setPeriodStartDateIsRequired] = useState<
    boolean | null
  >(null);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [confirmationDialogContent, setConfirmationDialogContent] =
    useState<ConfirmationDialogContent>({
      title: "",
      contentText: "",
      confirmText: "",
      action: () => {},
    });
  const isReplacement = !!defaultValues.replaceWorkAgreementId;
  const canUseWorkAgreementDocument =
    !isReplacement && type !== WorkOfferType.Overlapping;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = async (fieldValues: FieldValues) => {
    const payload = createApiPayload(bundleId, fieldValues);
    setIsSubmitting(true);
    const promises: any[] = [];

    const payloadWithTalentId: OfferWorkAgreement = {
      ...payload,
      talentId: talent.id,
    };
    const hasWorkAgreementDocument =
      getValues("workAgreementDocumentId") !== null;
    if (hasWorkAgreementDocument && !hasSystemGeneratedDocument) {
      const payload = createAcceptWorkAgreementApiPayload(
        talent.id,
        bundleId,
        getValues(),
      );
      promises.push(doAcceptWorkAgreement(payload));
    } else {
      promises.push(postOfferWorkAgreement(payloadWithTalentId));
    }

    await Promise.all<Response[]>(promises)
      .then((fulfilled) => {
        if (fulfilled.length > 1) {
          if (hasWorkAgreementDocument && !hasSystemGeneratedDocument) {
            sendMessage(t("order:assignmentCreatedSuccess"), "success");
          } else {
            sendMessage(
              t("order:multipleWorkAgreementsSentSuccess"),
              "success",
            );
          }
        } else {
          if (hasWorkAgreementDocument && !hasSystemGeneratedDocument) {
            sendMessage(t("order:assignmentCreatedSuccess"), "success");
          } else {
            sendMessage(t("order:workAgreementSentSuccess"), "success");
          }
        }
        // Clear replacement work agreement data after successful submission
        dispatch(clearReplacementWorkAgreementData());
      })
      .catch(() => {
        sendMessage(t("errors:internal_error"), "error");
      })
      .finally(() => {
        setIsSubmitting(false);
        navigateToOrderDetails();
      });
  };

  const onSubmitEdit = () => {
    const payload = createApiPayload(bundleId, getValues());
    const payloadWithAgreementId: UpdateWorkAgreement = {
      ...payload,
      workAgreementId: workAgreementId!!,
      version,
    };
    dispatch(updateWorkAgreement(payloadWithAgreementId)).then((action) => {
      if (isFulfilled(action)) {
        sendMessage(t("order:workAgreementUpdatedSuccess"), "success");
        navigateToOrderDetails();
      }
    });
  };

  const order = useAppSelector((state) =>
    selectOrderRowById(state, bundleId!!),
  );

  const onSubmitOverlappingAgreement = async (fieldValues: FieldValues) => {
    const payload = createAddAssignmentApiPayload(bundleId, fieldValues);
    setIsSubmitting(true);
    const promises: any[] = [];

    const payloadWithTalentId: AddAssignment = {
      ...payload,
      talentId: talent.id,
      // TODO at the moment we don't have orderRowId, and it seems it is not used really. So setting in fake.
      orderRowId: "just_a_copy", //order.orderRows[0].id,//. sourceAssignment!!.orderRowId,
      orderQueueId: order.orderQueueId,
      employmentId: sourceEmployment!!.employmentId,
      originalAssignmentId: sourceEmployment!!.assignments[0].assignmentId,
      customerOrgId: order.customerOrgId,
      customerCostCenterIds: order.customerCostCenterIds,
      contractId: order.contractId,
      workLocationId: order.workLocationId,
    };
    promises.push(postAddAssignment(payloadWithTalentId));

    let success = true;
    await Promise.all<Response[]>(promises)
      .then((fulfilled) => {
        if (fulfilled.length > 1) {
          sendMessage(t("order:multipleWorkAgreementsSentSuccess"), "success");
        } else {
          sendMessage(t("order:addAssignmentSentSuccess"), "success");
        }
      })
      .catch(() => {
        success = false;
        sendMessage(t("errors:internal_error"), "error");
      })
      .finally(() => {
        setIsSubmitting(false);
        if (success) navigateToOrderDetails();
      });
  };

  const confirmCancelWorkAgreementOffer = () => {
    setConfirmationDialogContent({
      title: t("order:cancelWorkAgreementOffer"),
      contentText: t("order:cancelWorkAgreementOfferConfirmation"),
      confirmText: t("order:cancelOffer"),
      action: onCancelWorkAgreement,
    });
    setIsConfirmationDialogOpen(true);
  };

  const doReplaceWorkAgreement = () => {
    setIsConfirmationDialogOpen(false);
    setConfirmationDialogContent({
      title: "",
      contentText: "",
      confirmText: "",
      action: () => {},
    });
    handleSubmit(onSubmit)();
  };

  const confirmReplaceWorkAgreement = () => {
    setConfirmationDialogContent({
      title: t("order:sendReplacementWorkAgreement"),
      contentText: t("order:sendReplacementWorkAgreementConfirmation"),
      confirmText: t("order:sendWorkOffer"),
      action: doReplaceWorkAgreement,
    });
    setIsConfirmationDialogOpen(true);
  };

  const onAcceptConfirmation = () => {
    if (workAgreementId) {
      return handleSubmit(onSubmitEdit)();
    } else {
      if (type === WorkOfferType.New) {
        if (isReplacement) {
          confirmReplaceWorkAgreement();
          return;
        }
        return handleSubmit(onSubmit)();
      } else if (type === WorkOfferType.Overlapping) {
        return handleSubmit(onSubmitOverlappingAgreement)();
      } else {
        sendMessage(`the mode ${type} is not supported`, "error");
      }
    }
  };

  const onCancelWorkAgreement = () => {
    if (workAgreementId) {
      dispatch(cancelWorkAgreementOffer({ workAgreementId, version })).then(
        (action) => {
          if (isFulfilled(action)) {
            sendMessage(t("order:workAgreementOfferCancelled"), "success");
            navigateToOrderDetails();
          }
        },
      );
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <OverlaySpinner isLoading={isSubmitting} />
        <BasicInfo />
        <TermsOfEmployment type={type} />
        {canUseWorkAgreementDocument && (
          <WorkAgreementDocument
            talentId={talent?.id}
            isReadOnly={isReadOnly || !!workAgreementId}
            setHasSystemGeneratedDocument={setHasSystemGeneratedDocument}
          />
        )}
        <WorkingTime
          periodStartDateIsRequired={periodStartDateIsRequired}
          type={type}
        />
        <Salary
          type={type}
          schedulingClassIds={schedulingClassIds}
          hasTargetedShiftSpecs={!!targetedShiftSpec}
        />
        {targetedShiftSpec && talent && (
          <WorkAgreementTargetedShifts
            targetedShiftSpec={targetedShiftSpec}
            contractId={order?.contractId}
            talentId={talent.id}
          />
        )}
        <Supplements
          setPeriodStartDateIsRequired={setPeriodStartDateIsRequired}
        />
        <SupplementPricingOverrides />
        {type === WorkOfferType.New && (
          <>
            <AdditionalInfo />
            <Message />
          </>
        )}
        <OverrideBillingPrice />
        {type === WorkOfferType.New && <CreateHrTaskWhenAccepted />}
        <FormActions
          onAccept={onAcceptConfirmation}
          onCancel={confirmCancelWorkAgreementOffer}
          isLoading={isSubmitting}
          type={type}
          hasSystemGeneratedDocument={hasSystemGeneratedDocument}
        />
      </FormProvider>
      <ConfirmationDialog
        title={confirmationDialogContent.title}
        contentText={confirmationDialogContent.contentText}
        cancelText={t("common:cancel")}
        confirmText={confirmationDialogContent.confirmText}
        isOpen={isConfirmationDialogOpen}
        onConfirm={confirmationDialogContent.action}
        onCancel={() => setIsConfirmationDialogOpen(false)}
      />
    </>
  );
};
